import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileSearch } from "lucide-react";

import { Badge, Button, Card, EmptyState, Mono, PageHeader, Text } from "@lagoon/ui";

import { api, type OrgAuditEvent } from "../api.js";
import {
  anyWithheld,
  auditAccess,
  AUDIT_PAGE_SIZE,
  describeAction,
  describeActor,
  describePage,
} from "../audit.js";
import { Failure, LoadingPanel, Notice } from "../components/feedback.js";
import type { Org } from "../orgs.js";

const header = (
  <PageHeader
    eyebrow="Record"
    title="Audit trail"
    description="Everything that has happened in this organisation, newest first. Nothing here can be edited or removed, including by us."
  />
);

/**
 * The organisation's own audit trail (LGN-146).
 *
 * A Team-plan customer has been paying for `entitlements.audit_export` since
 * Wave 10, the route behind it has been serving since LGN-71, and until now
 * nothing in the dashboard read either. This is the reader.
 *
 * Modelled on the console's `AuditScreen` deliberately rather than designed
 * afresh, but it is not the same screen and must not become it. Two
 * differences, both on purpose:
 *
 *   - **It is a reader and offers nothing else.** `audit_events` is
 *     append-only by construction, so there is no edit or delete to offer and
 *     no control here that would want one.
 *   - **It shows the customer's view, not the operator's.** The platform
 *     already withholds the forensic columns and, on rows an operator wrote,
 *     the reason, the before/after state and the staff address
 *     (`orgAuditView`). This screen renders what arrives and adds a line
 *     saying that something was held back, so a support action does not read
 *     as an empty row.
 *
 * The screen takes no filters, which is the LGN-125 lesson applied rather than
 * ignored: the console's stale `siteId` survived a navigation and showed one
 * site's rows under another's heading. The one piece of state that could go
 * stale here is the paging cursor across an organisation switch, and the shell
 * keys this component on the org id so the cursor is discarded with it. Ids
 * from one org are meaningless in another, and a cursor that survived the
 * switch would silently page into nothing.
 */
export function Audit(props: {
  org: Org;
  entitlements: { auditExport: boolean } | undefined;
  /** From `branding.ts`, via the shell. Never a literal in this file. */
  productName: string;
}): React.ReactElement {
  /**
   * The cursor stack: one `before` id per page the reader has gone past.
   *
   * A stack rather than a page number, because the route is keyset-paged. The
   * table is append-only, so ids are monotonic and an offset would drift as
   * rows landed underneath the reader.
   */
  const [pages, setPages] = useState<number[]>([]);
  const before = pages.at(-1);

  const access = auditAccess(props.org.role, props.entitlements);

  const trail = useQuery({
    queryKey: ["org-audit", props.org.id, before ?? null],
    queryFn: () =>
      api.orgAudit(props.org.id, {
        limit: AUDIT_PAGE_SIZE,
        ...(before === undefined ? {} : { before }),
      }),
    /**
     * Not asked at all unless the gate is open. Spending a request to be told
     * 403 would paint a red box over a screen whose real answer is a plan.
     *
     * The exception that matters is the other direction: an organisation whose
     * plan lapses while somebody is on this screen. The entitlements query
     * refetches on its own interval, `access` turns to `not-entitled`, and the
     * branch below explains it. If the audit request is the one that notices
     * first, its 403 carries the platform's own sentence and `Failure` shows
     * exactly that. Either way there is a sentence, and never a blank screen.
     */
    enabled: access === "ok",
  });

  if (access === "loading") {
    return <LoadingPanel rows={4} label="Checking your plan" />;
  }

  if (access !== "ok") {
    return (
      <div className="lg:flex lg:flex-col lg:gap-6">
        {header}
        <Notice tone="info">
          {access === "not-permitted"
            ? "The audit trail is open to owners and admins. Ask an owner of this organisation if you need it."
            : "The audit trail is part of the Team plan and above. Your organisation is not on one of those plans, so there is nothing to show here."}
        </Notice>
      </div>
    );
  }

  return (
    <div className="lg:flex lg:flex-col lg:gap-6">
      {header}

      {trail.isPending ? <LoadingPanel rows={5} label="Loading the audit trail" /> : null}

      {trail.isError ? (
        // The platform's own sentence when it wrote one, which is what a
        // lapsed plan arrives as: "The audit trail is available on the Team
        // plan and above."
        <Failure error={trail.error} fallback="Could not load the audit trail." />
      ) : null}

      {trail.data === undefined ? null : trail.data.events.length === 0 ? (
        <EmptyState
          icon={<FileSearch />}
          title="Nothing recorded yet"
          description="Invitations, role changes, sites and deletions all appear here as they happen."
          glow={false}
        />
      ) : (
        <>
          <ol className="lg:flex lg:flex-col lg:gap-3">
            {trail.data.events.map((event) => (
              <AuditRow key={event.id} event={event} productName={props.productName} />
            ))}
          </ol>

          {/*
            The paging sentence, always drawn and never only when there is a
            next page. A full page IS a truncated page, and one that does not
            say so reads as the whole of the record (LGN-125).
          */}
          <Text size="caption" tone="subtle">
            {describePage({
              count: trail.data.events.length,
              nextBefore: trail.data.nextBefore,
              page: pages.length,
            })}
          </Text>

          {anyWithheld(trail.data.events) ? (
            <Text size="caption" tone="subtle">
              {`Some rows were written by ${props.productName} rather than by your organisation. What changed and when is shown; the internal notes behind them stay with ${props.productName}. Ask support about any row here and they can explain it.`}
            </Text>
          ) : null}

          <div className="lg:flex lg:gap-3">
            {pages.length > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPages((stack) => stack.slice(0, -1));
                }}
              >
                Newer
              </Button>
            ) : null}
            {trail.data.nextBefore !== null ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const next = trail.data.nextBefore;
                  if (next !== null) setPages((stack) => [...stack, next]);
                }}
              >
                Older
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One row.
 *
 * The detail is expanded inline as it is in the console, and for the same
 * reason: a JSON blob per row would make forty rows unreadable, and the
 * question a reader arrives with is "what happened and when", not "what were
 * the exact column values". It only appears at all on rows the organisation
 * wrote itself, because on every other row the platform sent nothing to show.
 */
function AuditRow({
  event,
  productName,
}: {
  event: OrgAuditEvent;
  productName: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const detail = event.before !== null || event.after !== null;
  const sentence = describeAction(event.action);

  return (
    <li>
      <Card padded={false} interactive={false} className="lg:px-5 lg:py-4">
        <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-3">
          <Mono as="time" className="lg:text-caption lg:text-subtle">
            {new Date(event.occurredAt).toLocaleString()}
          </Mono>
          <Badge tone="neutral">{event.action}</Badge>
          <Text size="caption" tone="muted" as="span">
            {describeActor(event, productName)}
          </Text>
          {detail ? (
            <Button
              variant="ghost"
              size="sm"
              className="lg:ml-auto"
              aria-expanded={open}
              onClick={() => {
                setOpen(!open);
              }}
            >
              {open ? "Hide" : "Detail"}
            </Button>
          ) : null}
        </div>

        {sentence === null ? null : (
          <Text size="sm" className="lg:mt-2 lg:max-w-none">
            {sentence}
          </Text>
        )}

        {event.reason === null ? null : (
          <Text size="sm" className="lg:mt-2 lg:max-w-none">
            <strong className="lg:text-strong">Reason:</strong> {event.reason}
          </Text>
        )}

        {event.siteId === null ? null : (
          <Text size="caption" tone="subtle" className="lg:mt-2">
            <a href={`/sites/${event.siteId}`} className="lg:text-primary lg:hover:underline">
              the site involved
            </a>
          </Text>
        )}

        {open ? (
          <pre className="lg:mt-3 lg:overflow-x-auto lg:rounded-sm lg:bg-brand-bg2 lg:p-3 lg:font-mono lg:text-caption lg:text-muted-foreground">
            {JSON.stringify({ before: event.before, after: event.after }, null, 2)}
          </pre>
        ) : null}
      </Card>
    </li>
  );
}
